// Every date and countdown on screen is São Paulo time (A11) — never the browser's time zone. Calendar dates from the
// API (`YYYY-MM-DD`: due dates, cycles) are civil dates and are never shifted; instants (ISO timestamps) are shown as
// São Paulo wall-clock time.

export const TZ = 'America/Sao_Paulo';

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** The calendar day an instant falls on in São Paulo, as `YYYY-MM-DD`. */
export function dayInSaoPaulo(instant: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(instant);
}

function civil(day: string): { y: number; m: number; d: number } {
  const m = ISO_DAY.exec(day);
  if (!m) throw new RangeError(`not a YYYY-MM-DD date: ${day}`);
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

/** A civil date as a UTC-noon instant, so formatting it in UTC can never slip a day. */
const noonUtc = (day: string) => {
  const { y, m, d } = civil(day);
  return new Date(Date.UTC(y, m - 1, d, 12));
};

/** `DD/MM/AAAA` — a civil date as is, or the São Paulo day of an instant. */
export function formatDate(value: string | Date): string {
  const day = typeof value === 'string' ? value : dayInSaoPaulo(value);
  const { y, m, d } = civil(day);
  return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
}

/** `DD/MM` — short form for lists. */
export const formatShortDate = (value: string | Date): string => formatDate(value).slice(0, 5);

/** `sábado, 18 de outubro de 2026` — the page header's date. */
export function formatLongDate(value: string | Date): string {
  const day = typeof value === 'string' ? value : dayInSaoPaulo(value);
  return new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(noonUtc(day));
}

/** `06:14` — the time of an instant in São Paulo. */
export function formatTime(instant: Date | string): string {
  const at = typeof instant === 'string' ? new Date(instant) : instant;
  return new Intl.DateTimeFormat('pt-BR', { timeZone: TZ, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(at);
}

/** `18/10 · 06:14` — when a source last ran, in São Paulo time. */
export function formatDateTime(instant: Date | string): string {
  const at = typeof instant === 'string' ? new Date(instant) : instant;
  return `${formatShortDate(at)} · ${formatTime(at)}`;
}

/** Whole calendar days from São Paulo's today to `dueOn` (negative once overdue). */
export function countdownDays(dueOn: string, now: Date): number {
  return Math.round((noonUtc(dueOn).getTime() - noonUtc(dayInSaoPaulo(now)).getTime()) / 86_400_000);
}
